"use client";

import { Fragment, useState } from "react";

import { api, apiDownload } from "@/lib/api/client";
import type { AuditCrawl, AuditIssueUrls } from "@/lib/api/types";
import { type Category, SEVERITY_CLASS, SEVERITY_LABEL, formatDelta, issuesIn } from "@/lib/audit";
import { saveBlob } from "@/lib/download";
import { formatCount } from "@/lib/format";

type Props = {
  crawl: AuditCrawl;
  projectId: number;
  category: Category;
};

const TH = "px-3 py-2 font-medium";

type Loaded = { key: string; data: AuditIssueUrls | null; error: string | null };

/** Issues eines Crawls. Zeilen mit Treffern klappen die betroffenen URLs auf (bis zu 2.000, die Zahl ist exakt). */
export function IssueTable({ crawl, projectId, category }: Props) {
  const [open, setOpen] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const all = issuesIn(crawl, category);
  const hits = all.filter((issue) => issue.count > 0);
  const clean = all.filter((issue) => issue.count === 0);
  const base = `/api/audit/crawls/${crawl.id}/issues`;

  const toggle = (key: string) => {
    if (open === key) {
      setOpen(null);
      return;
    }
    setOpen(key);
    setLoaded(null);
    api<AuditIssueUrls>(`${base}/${key}?project_id=${projectId}`)
      .then((data) => setLoaded({ key, data, error: null }))
      .catch((caught: unknown) => setLoaded({ key, data: null, error: caught instanceof Error ? caught.message : "Unbekannter Fehler" }));
  };

  const exportCsv = async (key: string) => {
    setExportError(null);
    try {
      const file = await apiDownload(`${base}/${key}/export.csv?project_id=${projectId}`, `audit-${key}.csv`);
      saveBlob(file.blob, file.filename);
    } catch (caught) {
      setExportError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto">
        <table aria-label="Issues" className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs text-muted">
              <th className={TH}>Schwere</th>
              <th className={TH}>Issue</th>
              <th className={TH}>Kategorie</th>
              <th className={`${TH} text-right`}>URLs</th>
              <th className={`${TH} text-right`}>Δ zum Vorgänger</th>
              <th className={TH}>
                <span className="sr-only">URLs</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {hits.map((issue) => (
              <Fragment key={issue.key}>
                <tr className="border-t border-neutral-bg align-top">
                  <td className="px-3 py-2.5">
                    <span className={`rounded px-2 py-0.5 text-xs font-medium ${SEVERITY_CLASS[issue.severity]}`}>{SEVERITY_LABEL[issue.severity]}</span>
                  </td>
                  <td className="px-3 py-2.5 text-[13px]">
                    {issue.title}
                    {issue.hint && <div className="text-xs text-muted">{issue.hint}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-[13px] text-muted">{issue.category}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-[13px]">{formatCount(issue.count)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-[13px]">{formatDelta(issue.delta)}</td>
                  <td className="px-3 py-1 text-right">
                    <button
                      type="button"
                      aria-expanded={open === issue.key}
                      aria-label={`URLs zu ${issue.title} ${open === issue.key ? "ausblenden" : "zeigen"}`}
                      onClick={() => toggle(issue.key)}
                      className="h-11 rounded-md border border-field bg-white px-3 text-[13px]"
                    >
                      {open === issue.key ? "Ausblenden" : "URLs zeigen"}
                    </button>
                  </td>
                </tr>
                {open === issue.key && (
                  <tr className="bg-[#f7f8f7]">
                    <td colSpan={6} className="px-3 py-3">
                      {loaded?.key !== issue.key && <span className="text-[13px] text-muted">URLs werden geladen …</span>}
                      {loaded?.key === issue.key && loaded.error && (
                        <span role="alert" className="text-[13px] text-danger">
                          {loaded.error}
                        </span>
                      )}
                      {loaded?.key === issue.key && loaded.data && (
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted">
                            <span>
                              {loaded.data.urls.length < loaded.data.count
                                ? `Die ersten ${formatCount(loaded.data.urls.length)} von ${formatCount(loaded.data.count)} URLs`
                                : `${formatCount(loaded.data.count)} ${loaded.data.count === 1 ? "URL" : "URLs"}`}
                            </span>
                            <button
                              type="button"
                              onClick={() => void exportCsv(issue.key)}
                              className="h-11 rounded-md border border-field bg-white px-3 text-[13px] text-ink"
                            >
                              Als CSV exportieren
                            </button>
                          </div>
                          <ul aria-label={`URLs zu ${issue.title}`} className="max-h-72 overflow-y-auto break-all font-mono text-xs">
                            {loaded.data.urls.map((url) => (
                              <li key={url} className="py-0.5">
                                {url}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {hits.length === 0 && (
              <tr className="border-t border-neutral-bg">
                <td colSpan={6} className="px-3 py-4 text-sm text-muted">
                  Keine Treffer in dieser Kategorie.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {exportError && (
        <p role="alert" className="text-[13px] text-danger">
          {exportError}
        </p>
      )}
      {clean.length > 0 && (
        <details className="text-[13px] text-muted">
          <summary className="min-h-11 cursor-pointer py-2.5">Geprüft ohne Befund ({formatCount(clean.length)})</summary>
          <ul className="list-disc pl-5">
            {clean.map((issue) => (
              <li key={issue.key}>{issue.title}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
